import { ContentActionsItemType, RulesCondition } from '@usertour/types';
import { ActionHandler, ActionHandlerContext } from './action-handler.interface';
import { logger } from '@/utils';

const log = logger.scope('action');
/**
 * Manages action handlers and executes actions
 */
export class ActionManager {
  private handlers: ActionHandler[] = [];

  /**
   * Register an action handler
   * @param handler - The handler to register
   */
  registerHandler(handler: ActionHandler): void {
    this.handlers.push(handler);
  }

  /**
   * Register multiple action handlers
   * @param handlers - Array of handlers to register
   */
  registerHandlers(handlers: ActionHandler[]): void {
    this.handlers.push(...handlers);
  }

  /**
   * Clear all registered handlers
   */
  clearHandlers(): void {
    this.handlers = [];
  }

  /**
   * Handle a list of actions
   * @param actions - The actions to handle
   * @param context - The context object with component methods and source
   */
  async handleActions(actions: RulesCondition[], context: ActionHandlerContext): Promise<void> {
    // Separate actions by type
    const pageNavigateActions = actions.filter(
      (action) => action.type === ContentActionsItemType.PAGE_NAVIGATE,
    );
    const otherActions = actions.filter(
      (action) => action.type !== ContentActionsItemType.PAGE_NAVIGATE,
    );

    // Execute other actions first, then navigation actions
    await this.executeActions(otherActions, context);
    await this.executeActions(pageNavigateActions, context);
  }

  /**
   * Execute a list of actions in sequence
   * @param actions - The actions to execute
   * @param context - The context object with component methods and source
   */
  private async executeActions(
    actions: RulesCondition[],
    context: ActionHandlerContext,
  ): Promise<void> {
    for (const action of actions) {
      await this.executeAction(action, context);
    }
  }

  /**
   * Execute a single action
   * Wrapped in try-catch to prevent one failing action from stopping subsequent actions
   * @param action - The action to execute
   * @param context - The context object with component methods and source
   */
  private async executeAction(
    action: RulesCondition,
    context: ActionHandlerContext,
  ): Promise<void> {
    const handler = this.findHandler(action.type as ContentActionsItemType);
    if (handler) {
      try {
        await handler.handle(action, context);
      } catch (error) {
        log.error(`Failed to run action ${action.type}`, error);
      }
    } else {
      log.warn(`No handler for action ${action.type}; ignoring it`);
    }
  }

  /**
   * Find a handler that can handle the given action type
   * @param actionType - The action type to find a handler for
   * @returns The handler that can handle the action type, or undefined if none found
   */
  private findHandler(actionType: ContentActionsItemType): ActionHandler | undefined {
    return this.handlers.find((handler) => handler.canHandle(actionType));
  }
}
